<template>

  <div id="top" class="private-add-page">
    <div class="private-add-intro">
      <UiPageHeader title="Add Client" description="Capture the client profile, confirm operating settings, and complete the onboarding review.">
        <template #actions>
          <router-link :to="{ name: 'private' }" class="private-add-back"><i class="material-icons" aria-hidden="true">arrow_back</i>All clients</router-link>
        </template>
      </UiPageHeader>
    </div>
    <ol class="private-add-steps" aria-label="Client onboarding progress">
      <li v-for="(step, index) in onboardingSteps" :key="step.id" :class="{ 'is-active': next === step.id, 'is-complete': index < activeStepIndex }" :aria-current="next === step.id ? 'step' : undefined">
        <span class="private-add-step-number">{{ index + 1 }}</span><span>{{ step.title }}</span>
      </li>
    </ol>
    <div class="client-add-workbench sba-slide-in">
      <section class="client-add-body">
        <ProfileDiv :cat="cat" :newClient="newClient" @next="handleNext" v-if="next == 'start'" />
        <StatusDiv :cat="cat" :newClient="newClient" @next="handleNext" @back="handleBack" v-if="next == 'status'" />
        <SettingsDiv :cat="cat" :newClient="newClient" @next="handleNext"  @back="handleBack" v-if="next == 'settings'" />
        <ReviewDiv :cat="cat" :newClient="newClient" @next="handleNext" @back="handleBack" v-if="next == 'review'" />
        <PolicyDiv :cat="cat" :newClient="newClient" @next="handleNext" v-if="next == 'policy'" />
      </section>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watchEffect, computed } from "vue";
import { useRouter } from "vue-router";
import useModuleNavigation from "@/composables/useModuleNavigation";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import ProfileDiv from "@/components/client/onboard/ProfileOnboardComponent.vue";
import StatusDiv from "@/components/client/onboard/StatusOnboardComponent.vue";
import SettingsDiv from "@/components/client/onboard/SettingsOnboardComponent.vue";
import ReviewDiv from "@/components/client/onboard/ReviewOnboardComponent.vue";
import PolicyDiv from "@/components/client/onboard/PolicyOnboardComponent.vue";
import nativeUi from "@/ui/native-ui";

export default {
  name: "privateClientAdd",
  components: { UiPageHeader, ProfileDiv, ReviewDiv, StatusDiv, SettingsDiv, PolicyDiv },
  setup() {
    const home = ref(null);
    const cat = ref("hmo");
    const next = ref("start");
    const onboardingSteps = [
      { id: "start", title: "Profile" },
      { id: "status", title: "Operating status" },
      { id: "settings", title: "Settings" },
      { id: "review", title: "Review" },
      { id: "policy", title: "Policy" },
    ];
    const activeStepIndex = computed(() => onboardingSteps.findIndex(step => step.id === next.value));
    const newClient = ref({});
    const currTab = ref(null);
    const tabs = ref([]);

    // vue router
    const router = useRouter();
    const { routeToTab } = useModuleNavigation();

    // start
    onMounted(() => {
      // page name
      document.title = "Add (Private) Client";
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Clients", title: "All", icon: "arrow_back", url: "private", hash: null, });
      tabs.value.push({ id: 2, name: "Clients", title: "View", icon: "visibility", url: "private", hash: "#view" });
      tabs.value.push({ id: 3, name: "Clients", title: "Add Client", icon: null, url: null, hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("Add"));
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // handle curr status
    const handleActive = (tab) => {
      routeToTab(tab, { replace: !tab?.url });
    };

    // Title
    watchEffect(() => {
      if (newClient.value) {
        // load title
        setTimeout(() => {
          var elems = document.querySelectorAll(".tooltipped");
          nativeUi.Tooltip.init(elems, {});
        }, 2000);
      }
    });

    // UI modification
    const maxRule = (text) => {
      let maxLength = 15;
      if (text.length > maxLength) {
        return ` ${text.substring(0, maxLength)} ... `;
      } else {
        return ` ${text} `;
      }
    };

    // handle next
    const handleNext = (val) => {
      if (val.id == "profile") {
        // save profile
        newClient.value.profile = val.value;
        // next
        next.value = "status";
      } else if (val.id == "status") {
        // save status
        newClient.value.status = val.value;
        // next
        next.value = "settings";
      } else if (val.id == "settings") {
        // save status
        newClient.value.settings = val.value;
        // next
        next.value = "review";
      } else if (val.id == "review") {
        // account is created in review
        // save created
        newClient.value.created = val.value;
        //next
        next.value = "policy";
      } else if (val.id == "policy") {
        // finish
       router.push({ name: "privateView", params: { id: newClient.value.created.id } });
      }
       // top
       if (val.id != "policy") {
       router.push({ hash: "#top" });
       }
    };

    // handle next
    const handleBack = (val) => {
      if (val.id == "profile") {
        // no back button
      } else if (val.id == "status") {
        // back
        next.value = "start";
      } else if (val.id == "settings") {
        // back
        next.value = "status";
      } else if (val.id == "review") {
        // back
        next.value = "settings";
      } else if (val.id == "policy") {
        // no back button
      }
       // top
       router.push({ hash: "#top" });
    };


    return {
      onboardingSteps,
      activeStepIndex,
      home,
      handleActive,
      currTab,
      tabs,
      cat,
      next,
      maxRule,
      handleNext,
      handleBack,
      newClient,
    };
  },
};
</script>

<style scoped>
.private-add-page { box-sizing: border-box; margin: 0 auto; max-width: var(--ui-content-width); padding: 2rem 2rem 3rem; width: 100%; }
.private-add-intro { border-bottom: 1px solid var(--ui-border); margin-bottom: 2rem; padding-bottom: 1.75rem; }
.private-add-intro :deep(.ui-page-header) { margin-bottom: 0; }
.private-add-intro :deep(.ui-page-header__title) { font-size: clamp(1.75rem, 2.1vw, 2rem); font-weight: 700; letter-spacing: -.025em; }
.private-add-intro :deep(.ui-page-header__description) { font-size: 1rem; line-height: 1.55; }
.private-add-back { align-items: center; background: var(--ui-primary); border: 1px solid var(--ui-primary); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-primary-foreground); display: inline-flex; font-size: .8125rem; font-weight: 600; gap: .5rem; min-height: 2.5rem; padding: 0 .9rem; text-decoration: none; white-space: nowrap; }
.private-add-back i { font-size: 1rem; }
.private-add-steps { border-bottom: 1px solid var(--ui-border); display: flex; flex-wrap: wrap; gap: 1.5rem; list-style: none; margin: 0 0 1.5rem; padding: 0 0 1.25rem; }
.private-add-steps li { align-items: center; color: var(--ui-muted-foreground); display: flex; font-size: .8125rem; gap: .5rem; }
.private-add-step-number { align-items: center; border: 1px solid var(--ui-border); border-radius: 999px; display: flex; height: 1.75rem; justify-content: center; width: 1.75rem; }
.private-add-steps .is-active { color: var(--ui-foreground); font-weight: 600; }
.private-add-steps .is-active .private-add-step-number { background: var(--ui-primary); border-color: var(--ui-primary); color: var(--ui-primary-foreground); }
.private-add-steps .is-complete .private-add-step-number { background: var(--ui-muted); }
.private-add-page :is(a, button):focus-visible { outline: 2px solid var(--ui-ring); outline-offset: 2px; }

@layer components {
  .private-add-page .client-add-body { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; padding: 0 !important; }
  .private-add-page :deep(form > .section) { padding: 0 !important; }
  .private-add-page :deep(form > .section > .card) { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
  .private-add-page :deep(.card-content) { padding: 1.25rem 0 .5rem !important; }
  .private-add-page :deep(.card-title) { align-items: center; color: var(--ui-foreground) !important; display: flex; font-size: 1rem !important; font-weight: 600 !important; gap: .75rem; margin: 0 !important; }
  .private-add-page :deep(.card-title > .chip) { display: none !important; }
  .private-add-page :deep(.card-title > i) { font-size: 1rem !important; margin-left: auto !important; }
  .private-add-page :deep(.card-panel) { border: 0 !important; border-bottom: 1px solid var(--ui-border) !important; border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 1rem 0 !important; }
  .private-add-page :deep(.card-panel:not(.red)) { background: transparent !important; }
  .private-add-page :deep(.card-panel > .row) { display: grid !important; gap: 1rem !important; grid-template-columns: repeat(12, minmax(0, 1fr)) !important; margin: 0 !important; }
  .private-add-page :deep(.card-panel > .row > .col) { float: none !important; grid-column: span 12; margin: 0 !important; min-width: 0; padding: 0 !important; width: auto !important; }
  .private-add-page :deep(.card-panel > .row > .m7) { grid-column: span 7; }
  .private-add-page :deep(.card-panel > .row > .m5) { grid-column: span 5; }
  .private-add-page :deep(.card-panel > .row > .m6) { grid-column: span 6; }
  .private-add-page :deep(.card-panel > .row > .m4) { grid-column: span 4; }
  .private-add-page :deep(.card-panel > .row > .m3) { grid-column: span 3; }
  .private-add-page :deep(.card-panel > .row > .m2) { grid-column: span 2; }
  .private-add-page :deep(.card-panel > .row > .m1) { grid-column: span 1; }
  .private-add-page :deep(.input-field:not(.file-field)) { display: flex !important; flex-direction: column !important; margin: 0 !important; }
  .private-add-page :deep(.input-field > label) { color: var(--ui-foreground) !important; font-size: .8125rem !important; font-weight: 500 !important; margin: 0 0 .5rem !important; order: -1; position: static !important; transform: none !important; }
  .private-add-page :deep(.input-field > input), .private-add-page :deep(.input-field > select) { box-sizing: border-box !important; margin: 0 !important; min-height: 2.65rem !important; width: 100% !important; }
  .private-add-page :deep(.onboard-action-row), .private-add-page :deep(.status-action-row) { align-items: center !important; background: transparent !important; border: 0 !important; display: flex !important; flex-wrap: wrap !important; gap: .75rem !important; margin: 0 !important; padding: 1.25rem 0 0 !important; }
  .private-add-page :deep(.onboard-action-row > :is(a, button)), .private-add-page :deep(.status-action-row > :is(a, button)) { box-shadow: none !important; float: none !important; font-size: .8125rem !important; font-weight: 600 !important; gap: .5rem !important; margin: 0 !important; min-height: 2.5rem !important; padding: 0 1rem !important; text-decoration: none !important; white-space: nowrap; width: auto !important; }
  .private-add-page :deep(.onboard-action-row i), .private-add-page :deep(.status-action-row i) { font-size: 1rem !important; margin: 0 !important; }
  @media (max-width: 700px) { .private-add-page :deep(.card-panel > .row > .col) { grid-column: span 12; } }
}
@media (max-width: 700px) { .private-add-page { padding: 1.25rem 1rem 2rem; } .private-add-steps { gap: .75rem 1rem; } }

.private-add-breadcrumb {
  background: #fafafa !important;
  position: relative;
}

.private-add-breadcrumb::after {
  background: #c4d3d9;
  bottom: 0;
  content: "";
  height: 1px;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
}

.client-add-page-shell {
  margin-top: -14px;
  padding-bottom: 44px;
  padding-top: 0;
  position: relative;
  z-index: 1;
}

.client-add-workbench {
  color: #263238;
}

.client-add-head,
.client-add-body,
.client-add-workbench :deep(.card),
.client-add-workbench :deep(.card-panel),
.client-add-workbench :deep(.onboard-action-row) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.client-add-head {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  margin-bottom: 0;
  padding: 24px 26px;
  position: relative;
  z-index: 2;
}

.client-add-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.client-add-title-block {
  position: relative;
  z-index: 2;
}

.client-add-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.client-add-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 760px;
}

.client-add-body {
  background: #f5f7f8;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  padding: 22px 18px 18px;
  position: relative;
  z-index: 1;
}

.client-add-body :deep(form) {
  margin: 0;
}

.client-add-body :deep(.section) {
  padding-bottom: 0.55rem;
  padding-top: 0.55rem;
}

.client-add-body :deep(.card),
.client-add-body :deep(.card-panel) {
  background: #ffffff !important;
  border: 1px solid #dde6e9;
  border-radius: 6px;
  box-shadow: none;
}

.client-add-body :deep(.card) {
  overflow: visible;
}

.client-add-body :deep(.card-content) {
  border-bottom: 1px solid #edf2f3;
  padding: 18px 22px 14px;
}

.client-add-body :deep(.card-title) {
  align-items: center;
  color: #00695c !important;
  display: flex;
  font-size: 1.08rem;
  font-weight: 700;
  gap: 10px;
  line-height: 1.25;
}

.client-add-body :deep(.card-title .material-icons.right) {
  margin-left: auto;
}

.client-add-body :deep(.card-panel) {
  margin: 14px 18px;
  padding: 18px 20px;
}

.client-add-body :deep(.card-panel .row:last-child),
.client-add-body :deep(.card-panel p:last-child) {
  margin-bottom: 0;
}

.client-add-body :deep(.chip) {
  background: #e8f5f3;
  border: 1px solid #b8dfda;
  color: #00695c;
  font-size: 0.75rem;
  font-weight: 700;
  height: 24px;
  line-height: 22px;
}

.client-add-body :deep(.input-field) {
  margin-bottom: 0.55rem;
  margin-top: 0.85rem;
}

.client-add-body :deep(input:not([type]):focus:not([readonly])),
.client-add-body :deep(input[type=text]:not(.browser-default):focus:not([readonly])),
.client-add-body :deep(input[type=email]:not(.browser-default):focus:not([readonly])),
.client-add-body :deep(input[type=number]:not(.browser-default):focus:not([readonly])) {
  border-bottom-color: #00897b;
  box-shadow: 0 1px 0 0 #00897b;
}

.client-add-body :deep(.autocomplete-content.dropdown-content) {
  z-index: 20;
}

.client-add-body :deep(.onboard-action-row) {
  align-items: center;
  border-top: 1px solid #edf2f3;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: 6px 18px 0;
  padding: 18px 0 4px;
}

.client-add-body :deep(.onboard-action-row.is-single) {
  justify-content: flex-end;
}

.client-add-body :deep(.onboard-action-row.is-policy) {
  justify-content: flex-start;
}

.client-add-body :deep(.onboard-action-row .modal-trigger:last-child) {
  margin-left: auto;
}

.client-add-body :deep(.section.center),
.client-add-body :deep(.section > .row) {
  margin-bottom: 0;
}

.client-add-body :deep(.btn-large) {
  border-radius: 5px;
  box-shadow: 0 10px 18px -16px rgba(38, 50, 56, 0.8);
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.client-add-body :deep(.btn-large.blue) {
  background: #00796b !important;
}

.client-add-body :deep(.btn-large.blue:hover) {
  background: #00695c !important;
}

.client-add-body :deep(.btn-large.black) {
  background: #37474f !important;
}

.client-add-body :deep(.btn-large.black:hover) {
  background: #263238 !important;
}

.pointer {
  cursor: default;
}

.custom-nav {
  height: 15px;
}

/* .nav-extended .nav-wrapper {
  padding: 0 20px;
} */

.brand-logo {
  font-weight: 300;
  font-size: 5rem;
}

.nav-wrapper ul li a {
  font-weight: 400;
  padding: 0 15px;
}

.nav-content {
  min-height: 20px;
}

@media only screen and (max-width: 600px) {
  .client-add-head {
    padding: 20px;
  }

  .client-add-title h5 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .client-add-body {
    padding: 18px 12px;
  }

  .client-add-body :deep(.card-content),
  .client-add-body :deep(.card-panel) {
    margin-left: 0;
    margin-right: 0;
    padding-left: 16px;
    padding-right: 16px;
  }

  .client-add-body :deep(.onboard-action-row) {
    align-items: stretch;
    flex-direction: column;
    margin-left: 0;
    margin-right: 0;
  }

  .client-add-body :deep(.onboard-action-row .modal-trigger:last-child) {
    margin-left: 0;
  }

  .client-add-body :deep(.btn-large),
  .client-add-body :deep(.modal-trigger) {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .client-add-head,
  .client-add-body,
  .client-add-workbench :deep(.card),
  .client-add-workbench :deep(.card-panel),
  .client-add-workbench :deep(.onboard-action-row) {
    animation: none;
  }
}
</style>
